import { Button, Input } from '@invent/wl-ui-kit-next/ui';
import * as React from 'react';
import { useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';

import {
  ChoiceSelect,
  CompactBar,
  CompactChipLine,
  CompactDivider,
  CompactTitle,
  DataTable,
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  HelpPopover,
  NotPermitted,
  PageHeader,
  SplitList,
  STICKY_TABLE_TOP,
  StatusChip,
  useScrolledPast,
  useWorkToolbar,
  WorkToolbar,
  type IDataColumn,
  type IDataGrouping
} from '@/design';
import { useCan } from '@/domains/auth';
import { formatDateTime } from '@/domains/survey';
import type {
  IExportCycle,
  IRankingEdition,
  IRankingKey
} from '../../api/ranking-process-contracts';
import { rankingProcessApi } from '../../api/ranking-process-api';
import type { IRankingList } from '../../api/ranking-contracts';
import { isSendableSegment, rankingsApi } from '../../api/rankings-api';
import { LoadError, useAdminData } from '../../components/admin-page';
import { formatDay } from '../../components/days';
import { ListArea, useListRereads } from '../../components/list-area';
import type { ICardLinkState } from '../../components/participant-link';
import { countOf, listSurveyLabel } from '../../lists';
import { TEAM_PATHS, rankingPath } from '../../paths';
import {
  EXPORTED_NOT_IMPORTED,
  countText,
  exportedNotImported,
  madeNotExported,
  rankingRefusalReason,
  rankingStatusChip
} from '../../rankings';
import { refusalMessage } from '../../refusal';
import { useKnownFunnels, type IKnownFilter } from '../lists/work-page/light-list';
import { SurveyMenu, SurveyTabs } from '../lists/area';
import { WorkPageHeaderActions, useReadAt } from '../lists/work-page/header-actions';

/**
 * The Rankings area, `/team/rankings` (SR-231): a survey's editions as a Lightning list view, and
 * one ranking's record (`ranking.tsx`) opened beside it in the split view — the refresh runs' way.
 *
 * **The survey is the URL's** (`?survey=`, FA by default) and the list is survey-api's, in its
 * order (newest year first, then the catalogue's), drawn as sent. `CANADA_FR` is not offered:
 * survey-api keeps its rankings under `CANADA`.
 *
 * **"Start an edition…" opens a ranking at its first step, Export for scoring** (the process order
 * of 19 Oct), for a list and year chosen here, whether or not the address holds a ranking yet — the
 * export makes the draft (SR-451), frozen and linked to its cycle; a scored result imported with no
 * export before it makes one too. It is `rankings.manage`'s, drawn disabled with the reason for
 * anyone else (SR-228's rule).
 *
 * **A light work page** (SR-515, the work pages design, "Three forms"): the title with its tabs
 * (the survey), the key number, *Refresh* handed to the app bar and the "?"; "Start an edition…"
 * the page's action; Cycle as the Cycle column's funnel and the filter line's chip. survey-api
 * answers a survey's editions whole and the cycle narrows the list as read —
 * the one filter here that is the browser's. The editions stay grouped by cycle, so there is no
 * pager: a survey holds a few dozen editions, and a page boundary would cut a cycle's group.
 *
 * **An edition exported and not imported yet is said so** (SR-451, the cost of the owner's choice
 * (A)): a draft with nothing placed and nothing imported reads "Exported, not imported yet" where
 * its count would be, `[data-exported]` on the cell — the truth of a file still being scored.
 */

export const RANKING_SURVEYS = ['FA', 'TEAMS', 'FSP', 'RIA_FIRM', 'CANADA'] as const;

const DEFAULT_SURVEY = 'FA';

/** The survey a list's search names — FA where it names none. */
function surveyOf(params: URLSearchParams): string {
  return params.get('survey') || DEFAULT_SURVEY;
}

/** An edition's key, as the pane's selection and the table's rows are keyed. */
function editionKey(edition: { surveyCode: string; list: string; year: number | string }): string {
  return `${edition.surveyCode}/${edition.list}/${edition.year}`;
}

/** The editions and a ranking, as one area. */
export function AdminRankingsArea() {
  return (
    <ListArea
      param="year"
      list={TEAM_PATHS.rankings}
      paneTitle="Rankings"
      pane={(search) => <EditionsPane search={search} />}
    />
  );
}

/** The survey tabs' address: FA is no key. */
function surveySearch(code: string): string {
  return code === DEFAULT_SURVEY ? '' : `?survey=${encodeURIComponent(code)}`;
}

const SURVEY_TABS = RANKING_SURVEYS.map((code) => ({ code, label: listSurveyLabel(code) }));

export function AdminRankingEditionsPage() {
  const [params, setParams] = useSearchParams();
  const location = useLocation();
  const navigate = useNavigate();
  const survey = surveyOf(params);
  const { data, error, reading, reload } = useAdminData<{ survey: string; editions: IRankingEdition[] }>(
    async (signal) => {
      try {
        return { survey, editions: await rankingProcessApi.editions(survey, signal) };
      } catch (readError) {
        throw new Error(refusalMessage(readError, 'Could not read the rankings'));
      }
    },
    [survey]
  );
  // The cycles pinning the survey (SR-451): what the editions are grouped by and filtered on.
  const cyclesRead = useAdminData<{ survey: string; cycles: IExportCycle[] }>(
    async (signal) => ({ survey, cycles: await rankingProcessApi.surveyCycles(survey, signal) }),
    [survey]
  );
  const cycles = cyclesRead.data;
  const known = React.useMemo(
    () => (cycles?.survey === survey ? cycles.cycles : []),
    [cycles, survey]
  );
  const cycleFilter = params.get('cycle');
  const all = data?.survey === survey ? data.editions : null;
  // ⚠ The one narrowing done in the browser: survey-api answers a survey's editions whole.
  const shown =
    all === null || cycleFilter === null
      ? all
      : all.filter((edition) => (edition.cycleCode ?? NO_CYCLE) === cycleFilter);
  const [collapsed, setCollapsed] = React.useState<ReadonlySet<string>>(new Set());
  // Cycle codes are shared across surveys: a fold is the survey's it was made on.
  React.useEffect(() => setCollapsed(new Set()), [survey]);
  const grouping = React.useMemo<IDataGrouping<IRankingEdition>>(
    () => editionsByCycle(known, collapsed, (key) =>
      setCollapsed((current) => {
        const next = new Set(current);
        if (!next.delete(key)) next.add(key);
        return next;
      })
    ),
    [known, collapsed]
  );
  const open = (edition: IRankingEdition) =>
    navigate(rankingPath(edition), {
      state: { search: location.search, list: true } satisfies ICardLinkState
    });

  const cycleFilterSpec: IKnownFilter = {
    param: 'cycle',
    column: 'cycle',
    label: 'Cycle',
    options: [
      ...known.map((cycle) => ({ value: cycle.code, label: `${cycle.name} (${cycle.code})` })),
      { value: NO_CYCLE, label: 'No cycle' }
    ],
    labelOf: (code) => {
      if (code === NO_CYCLE) return 'No cycle';
      const cycle = known.find((entry) => entry.code === code);
      return cycle ? cycle.name : code;
    },
    emptyIsValue: true
  };
  const choose = (name: string, value: Nullable<string>) =>
    setParams((current) => {
      const next = new URLSearchParams(current);
      if (value === null) next.delete(name);
      else next.set(name, value);
      return next;
    });
  const funnels = useKnownFunnels([cycleFilterSpec], params, choose);
  const clearAll = () => choose('cycle', null);
  const chips = funnels.chips;

  const readAt = useReadAt(data);
  const refresh = {
    reading: reading || cyclesRead.reading,
    readAt,
    onClick: () => {
      void reload();
      void cyclesRead.reload();
    }
  };
  const total = all?.length ?? null;
  const help = (
    <HelpPopover label="About Rankings" align="end">
      <p>
        A survey&apos;s ranking editions, one a list and year, grouped by the cycle each runs on —
        newest cycle first, the editions on none (migrated, or placed by hand) last. Open an edition
        for its steps: export for scoring, import the scores, place, publish.
      </p>
      <p>
        Start an edition chooses a list, a year and its cycle, fixed from then on. The Cycle
        column&apos;s funnel shows one cycle&apos;s editions, or those on none.
      </p>
    </HelpPopover>
  );
  const toolbar = useWorkToolbar();
  const [controlsEnd, setControlsEnd] = React.useState<Nullable<HTMLElement>>(null);
  const scrolled = useScrolledPast(controlsEnd);

  return (
    <>
      <PageHeader
        title="Rankings"
        row
        tabs={<SurveyTabs current={survey} surveys={SURVEY_TABS} searchOf={surveySearch} />}
        actions={
          <WorkPageHeaderActions
            summary={
              total === null ? null : countOf(total, 'edition')
            }
            summaryData={{ 'data-editions-summary': '' }}
            refresh={refresh}
            help={help}
          />
        }
      />
      <CompactBar
        shown={scrolled}
        label="Rankings, compact"
        actions={<WorkPageHeaderActions summary={null} refresh={refresh} help={help} />}
        below={chips.length > 0 && <CompactChipLine chips={chips} onClearAll={clearAll} />}
      >
        <CompactTitle>Rankings</CompactTitle>
        <SurveyMenu current={survey} surveys={SURVEY_TABS} searchOf={surveySearch} />
        <span className="flex-1" />
        <CompactDivider />
        <StartEdition survey={survey} compact />
        <CompactDivider />
      </CompactBar>
      <div className="grid min-w-0 gap-3 px-6 py-4">
        <WorkToolbar
          toolbar={toolbar}
          status={{ reading, count: shown ? `${shown.length.toLocaleString('en-US')} found` : undefined }}
          quick={null}
          quickCount={0}
          end={<StartEdition survey={survey} />}
        />
        {chips.length > 0 && <CompactChipLine label="Filters" chips={chips} onClearAll={clearAll} />}
        <div ref={setControlsEnd} aria-hidden data-controls-end="" className="-mt-3 h-0" />
        {error && !reading && all !== null && (
          <LoadError message={`Could not refresh — showing the editions as last read. ${error}`} />
        )}
        <DataTable
          label="Editions"
          columns={editionColumns(funnels.header(cycleFilterSpec))}
          grouping={grouping}
          rows={shown && shown.length > 0 ? shown : null}
          rowKey={editionKey}
          rowProps={(edition) =>
            ({ 'data-edition': editionKey(edition) }) as React.HTMLAttributes<HTMLTableRowElement>
          }
          onOpen={open}
          busy={reading && all !== null}
          state={
            all === null
              ? error && !reading
                ? { kind: 'error', content: <LoadError message={error} /> }
                : { kind: 'loading' }
              : {
                  kind: 'empty',
                  content:
                    cycleFilter === null
                      ? `No ${survey} ranking yet.`
                      : `No ${survey} ranking on this cycle yet.`
                }
          }
          stickyTop={STICKY_TABLE_TOP}
        />
      </div>
    </>
  );
}

/** A ranking's status as its chip — `[data-ranking-status]` is what a spec reads. */
export function RankingStatusChip({ status }: { status: string }) {
  const chip = rankingStatusChip(status);
  return (
    <span data-ranking-status={status}>
      <StatusChip tone={chip.tone} dot={status === 'draft'}>
        {chip.label}
      </StatusChip>
    </span>
  );
}

/** The table's columns, the Cycle column headed by its funnel. */
function editionColumns(cycleHeader: React.ReactNode): IDataColumn<IRankingEdition>[] {
  return [
    { id: 'name', header: 'Name', className: 'font-medium', cell: (edition) => edition.name },
    {
      id: 'cycle',
      header: cycleHeader,
      label: 'Cycle',
      className: 'text-xs tabular-nums text-muted-foreground',
      cell: (edition) => edition.cycleCode ?? '—'
    },
    { id: 'status', header: 'Status', cell: (edition) => <RankingStatusChip status={edition.status} /> },
    { id: 'stage', header: 'Stage', cell: (edition) => <EditionStage edition={edition} /> },
    {
      // The moment its figures are read at (SR-451): lists of one cycle with different snapshots
      // read different archives, which the cycle's heading says.
      id: 'snapshot',
      header: 'Snapshot',
      cell: (edition) =>
        edition.frozenAt ? (
          <span
            className="tabular-nums"
            data-snapshot-at={edition.frozenAt}
            title={new Date(edition.frozenAt).toLocaleString('en-US', {
              dateStyle: 'medium',
              timeStyle: 'medium'
            })}
          >
            {formatDateTime(edition.frozenAt)}
          </span>
        ) : (
          <span className="text-muted-foreground">—</span>
        )
    },
    {
      id: 'territories',
      header: 'Territories',
      numeric: true,
      cell: (edition) => (edition.territorial ? countText(edition.territories) : 'National')
    }
  ];
}

/** Where an edition stands in the process, in words: exported and waiting for its scores, scored,
 *  placed by hand, published — or nothing exported yet. */
function EditionStage({ edition }: { edition: IRankingEdition }) {
  if (exportedNotImported(edition)) {
    return (
      <span data-exported className="text-muted-foreground">
        {EXPORTED_NOT_IMPORTED}
      </span>
    );
  }
  const placed = `${countText(edition.placements)} placed`;
  if (edition.status === 'published') return <span>Published · {placed}</span>;
  if (edition.imported) return <span>Scored · {placed}</span>;
  if (madeNotExported(edition)) return <span className="text-muted-foreground">Not exported yet</span>;
  return <span>{placed}</span>;
}

/** The cycle an edition is offered on first: the newest open one with an as-of date, else the
 *  newest closed one, else the newest dated, else the newest — survey-api lists newest first. */
function defaultCycle(cycles: readonly IExportCycle[]): IExportCycle | undefined {
  const dated = cycles.filter((cycle) => cycle.asOfDate !== null);
  return (
    dated.find((cycle) => cycle.status === 'open') ??
    dated.find((cycle) => cycle.status === 'closed') ??
    dated[0] ??
    cycles[0]
  );
}

/** The group of the editions on no cycle: migrated from Salesforce, or placed by hand. */
const NO_CYCLE = '';

/** The editions grouped by their cycle (SR-451, RNK's "each ranking runs on its own cycle"):
 *  newest cycle first as survey-api lists them, the editions on none last. Each heading names
 *  the cycle, its status and as-of date, how many lists it holds, and — where its drafts were
 *  snapshotted at different moments — that they read different archives. */
function editionsByCycle(
  cycles: readonly IExportCycle[],
  collapsed: ReadonlySet<string>,
  onToggle: (key: string) => void
): IDataGrouping<IRankingEdition> {
  const order = new Map(cycles.map((cycle, index) => [cycle.code, index]));
  const rank = (code: string) => (code === NO_CYCLE ? Infinity : (order.get(code) ?? cycles.length));
  return {
    levels: [
      {
        id: 'cycle',
        label: 'Cycle',
        value: (edition) => edition.cycleCode ?? NO_CYCLE,
        compare: (left, right) => rank(left) - rank(right) || left.localeCompare(right),
        display: (code) => {
          if (code === NO_CYCLE) return 'none — migrated or placed by hand';
          const cycle = cycles.find((entry) => entry.code === code);
          if (!cycle) return code;
          return [
            cycle.name,
            cycle.status,
            cycle.asOfDate ? `figures as of ${formatDay(cycle.asOfDate)}` : 'no as-of date'
          ].join(' · ');
        }
      }
    ],
    collapsed,
    onToggle,
    count: (_path, rows) => {
      // Compared to the minute, the precision the Snapshot column shows: the mark is for lists
      // snapshotted at different points of the process, not seconds apart in one export run.
      const snapshots = new Set(
        rows
          .filter((edition) => edition.status === 'draft' && edition.frozenAt)
          .map((edition) => formatDateTime(edition.frozenAt!))
      );
      return (
        <span className="text-muted-foreground">
          ({rows.length} {rows.length === 1 ? 'list' : 'lists'})
          {snapshots.size > 1 && (
            <span className="ml-2 text-amber-700" data-snapshots-differ="">
              snapshots differ
            </span>
          )}
        </span>
      );
    }
  };
}

/** The editions beside an open ranking: of the survey the list was opened under, or the
 *  ranking's own. */
function EditionsPane({ search }: { search: string }) {
  const params = useParams();
  const fromList = new URLSearchParams(search).get('survey');
  const survey = fromList || params.survey || DEFAULT_SURVEY;
  const selected = editionKey({
    surveyCode: params.survey ?? '',
    list: params.list ?? '',
    year: params.year ?? ''
  });
  const rereads = useListRereads();
  const { data, error } = useAdminData<IRankingEdition[]>(
    (signal) => rankingProcessApi.editions(survey, signal),
    [survey, rereads]
  );
  return (
    <SplitList
      label="Editions"
      selected={selected}
      items={
        data?.map((edition) => ({
          key: editionKey(edition),
          to: rankingPath(edition),
          state: { search, list: true } satisfies ICardLinkState,
          primary: edition.name,
          secondary: exportedNotImported(edition)
            ? EXPORTED_NOT_IMPORTED
            : `${countText(edition.placements)} placed`,
          trailing: <RankingStatusChip status={edition.status} />
        })) ?? null
      }
      status={error ?? (data && data.length === 0 ? `No ${survey} ranking yet.` : undefined)}
    />
  );
}

/** "Start an edition…": a list of the survey's catalogue and a year, then that edition at its first
 *  step, Export for scoring — whether or not a ranking is kept there yet. */
function StartEdition({ survey, compact = false }: { survey: string; compact?: boolean }) {
  const permitted = useCan()('rankings.manage');
  const navigate = useNavigate();
  const reasonId = React.useId();
  const listLabel = React.useId();
  const [open, setOpen] = React.useState(false);
  const [lists, setLists] = React.useState<Nullable<IRankingList[]>>(null);
  const [failed, setFailed] = React.useState<Nullable<string>>(null);
  const [list, setList] = React.useState('');
  const [year, setYear] = React.useState(String(new Date().getFullYear() + 1));
  const cycleLabel = React.useId();
  // The cycle the edition is made on (SR-451): fixed from then on, so chosen here, newest dated
  // cycle first.
  const [cycles, setCycles] = React.useState<Nullable<IExportCycle[]>>(null);
  const [cycle, setCycle] = React.useState('');
  const [busy, setBusy] = React.useState(false);
  const [refused, setRefused] = React.useState<Nullable<string>>(null);
  // The edition already kept at the address on another cycle (SR-451's `ranking-exists`), with the
  // address it was refused for — what "Open it" opens, whatever the form says since.
  const [kept, setKept] = React.useState<
    Nullable<{ key: IRankingKey; name: string; cycleCode: Nullable<string> }>
  >(null);

  // A list is one survey's: another survey's catalogue replaces the choice, keeping it only where
  // the new catalogue holds it.
  React.useEffect(() => {
    setLists(null);
    setList('');
    setCycles(null);
    setCycle('');
  }, [survey]);
  // What a refusal said is about the address it was asked for: a change of it takes it away.
  React.useEffect(() => setKept(null), [open, list, year, cycle]);

  React.useEffect(() => {
    if (!open) return undefined;
    // An answer for a survey since switched away from is not this dialog's.
    let stale = false;
    setFailed(null);
    setRefused(null);
    setKept(null);
    rankingProcessApi.surveyCycles(survey).then(
      (pinned) => {
        if (stale) return;
        setCycles(pinned);
        setCycle((current) =>
          pinned.some((entry) => entry.code === current)
            ? current
            : (defaultCycle(pinned)?.code ?? '')
        );
      },
      (error: unknown) => {
        if (!stale) setFailed(refusalMessage(error, 'The cycles could not be read'));
      }
    );
    rankingsApi.lists(survey).then(
      (catalogue) => {
        if (stale) return;
        const offered = catalogue?.lists ?? [];
        setLists(offered);
        setList((current) =>
          offered.some((entry) => entry.list === current) ? current : (offered[0]?.list ?? '')
        );
      },
      (error: unknown) => {
        if (!stale) setFailed(refusalMessage(error, 'The lists could not be read'));
      }
    );
    return () => {
      stale = true;
    };
  }, [open, survey]);

  const go = async (event: React.FormEvent) => {
    event.preventDefault();
    const key = { surveyCode: survey, list, year: year.trim() };
    setBusy(true);
    setRefused(null);
    setKept(null);
    try {
      // Made on its cycle, or the edition already kept there on that cycle answered as it is.
      await rankingProcessApi.createEdition(key, cycle);
      setOpen(false);
      navigate(rankingPath(key, 'export'));
    } catch (failure) {
      if (rankingRefusalReason(failure) === 'ranking-exists') {
        // One edition per list and year: say which cycle it is on, and offer to open it.
        const read = await rankingProcessApi.overview(key).catch(() => null);
        setKept({
          key,
          name: read?.body.ranking.name ?? `${key.year} ${list}`,
          cycleCode: read?.body.ranking.cycleCode ?? null
        });
      } else {
        setRefused(refusalMessage(failure, 'The edition could not be made'));
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <span className="grid justify-items-end gap-1">
      <Button
        type="button"
        size="sm"
        disabled={!permitted}
        aria-describedby={permitted ? undefined : reasonId}
        onClick={() => setOpen(true)}
      >
        Start an edition…
      </Button>
      {!permitted && !compact && <NotPermitted id={reasonId} what="starting a ranking edition" />}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <form onSubmit={(event) => void go(event)} aria-label="Start an edition">
            <DialogHeader>
              <DialogTitle>Start an edition</DialogTitle>
              <DialogDescription>
                Which {survey} list and year, and the cycle it runs on — fixed from then on: its
                export, its snapshot and its scores are that cycle’s. It opens at its first step,
                Export for scoring; an edition already kept there opens as it is, on its own cycle.
              </DialogDescription>
            </DialogHeader>
            <DialogBody>
              {failed && <LoadError message={failed} />}
              <div className="grid gap-1 text-sm">
                <span id={listLabel} className="text-muted-foreground">
                  List
                </span>
                <ChoiceSelect
                  labelledBy={listLabel}
                  value={list}
                  onChange={setList}
                  choices={(lists ?? []).map((option) => ({
                    value: option.list,
                    label: option.label
                  }))}
                />
              </div>
              <label className="grid gap-1 text-sm">
                <span className="text-muted-foreground">Year</span>
                <Input
                  value={year}
                  inputMode="numeric"
                  onChange={(event) => setYear(event.target.value)}
                />
              </label>
              <div className="grid gap-1 text-sm">
                <span id={cycleLabel} className="text-muted-foreground">
                  Cycle
                </span>
                {cycles !== null && cycles.length === 0 ? (
                  <span className="text-muted-foreground" data-start-cycle="none">
                    No cycle carries {survey} yet: pin it to one on Setup → Cycles first.
                  </span>
                ) : (
                  <ChoiceSelect
                    labelledBy={cycleLabel}
                    value={cycle}
                    onChange={setCycle}
                    choices={(cycles ?? []).map((entry) => ({
                      value: entry.code,
                      label: `${entry.name} (${entry.code}) — ${
                        entry.asOfDate ? `figures as of ${formatDay(entry.asOfDate)}` : 'no as-of date'
                      }`
                    }))}
                  />
                )}
              </div>
              {refused && (
                <p role="alert" className="text-sm text-destructive">
                  {refused}
                </p>
              )}
              {kept && (
                <div
                  role="alert"
                  className="grid gap-2 rounded-md border border-amber-300 bg-amber-50 p-3 text-sm"
                  data-edition-kept={kept.cycleCode ?? ''}
                >
                  <span>
                    <strong>{kept.name}</strong> already exists
                    {kept.cycleCode ? ` on cycle ${kept.cycleCode}` : ' on no cycle'}. A list has one
                    edition a year, and its cycle is fixed: it cannot be made again on another cycle.
                  </span>
                  <span>
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      onClick={() => {
                        setOpen(false);
                        navigate(rankingPath(kept.key, 'export'));
                      }}
                    >
                      Open it
                    </Button>
                  </span>
                </div>
              )}
            </DialogBody>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setOpen(false)}>
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={busy || list === '' || cycle === '' || !isSendableSegment(year.trim())}
              >
                {busy ? 'Making…' : 'Continue'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </span>
  );
}
